/**
 * The use cases a business unit exposes.
 *
 * Not one of the four setup steps — a BU works without any — but it is what the What's New
 * creation flow filters by, so it belongs on the BU rather than hidden elsewhere.
 *
 * Only shown while editing: a use case is attached to a tenant that already exists.
 *
 * @module Pages/BUs/BUManager
 */

import { css } from '@emotion/css';
import { UTButton, UTLoading, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useActionToast } from '@/Hooks/useActionToast';
import { useState } from 'react';
import type { JSX } from 'react';
import { addTenantUseCase, getTenantUseCases, toggleTenantUseCase } from '../../Services';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';
import { Switch } from '@/Shared/components/Switch/Switch';

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-top: var(--space-m);
  border-top: 1px solid var(--color-divider);
`;

const titleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const hintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const listStyle = css`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  overflow: hidden;
`;

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-s) var(--space-m);
  border-bottom: 1px solid var(--color-divider);

  &:last-child {
    border-bottom: none;
  }
`;

const rowTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const nameStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const spacerStyle = css`
  flex: 1;
`;

const addRowStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(160px, 100%), 1fr));
  gap: var(--space-sm);
  align-items: end;
`;

const errorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
`;

const SLUG_PATTERN = /^[a-z][a-z0-9_-]{1,}$/;

export interface UseCaseSectionProps {
  tenantSlug: string;
}

export const UseCaseSection = ({ tenantSlug }: UseCaseSectionProps): JSX.Element => {
  const queryClient = useQueryClient();
  const toast = useActionToast();
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const queryKey = ['tenants', tenantSlug, 'use-cases'];

  const { data: useCases, isLoading } = useQuery({
    queryKey,
    queryFn: () => getTenantUseCases(tenantSlug),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const toggle = useMutation({
    mutationFn: (useCaseId: string) => toggleTenantUseCase(tenantSlug, useCaseId),
    onSuccess: () => {
      void invalidate();
      toast.success('Use case atualizado');
    },
    onError: (mutationError: Error) => {
      setError(mutationError.message || 'Não foi possível alterar o use case.');
      toast.failure('Não foi possível alterar o use case', mutationError);
    },
  });

  const add = useMutation({
    mutationFn: () => addTenantUseCase(tenantSlug, { slug: slug.trim(), name: name.trim() }),
    onSuccess: () => {
      toast.success(`Use case ${slug.trim()} adicionado`);
      setSlug('');
      setName('');
      setError(null);
      void invalidate();
    },
    onError: (mutationError: Error) => {
      setError(mutationError.message || 'Não foi possível adicionar o use case.');
      toast.failure('Não foi possível adicionar o use case', mutationError);
    },
  });

  const canAdd = SLUG_PATTERN.test(slug.trim()) && name.trim().length > 0;

  return (
    <div className={sectionStyle}>
      <div>
        <span className={titleStyle}>Use cases</span>
        <p className={hintStyle} style={{ margin: '2px 0 0' }}>
          Recortes dentro da BU. O What&apos;s New pode ser publicado para um use case específico em
          vez da BU inteira.
        </p>
      </div>

      {isLoading ? (
        <UTLoading type='dots' />
      ) : (useCases ?? []).length > 0 ? (
        <div className={listStyle}>
          {(useCases ?? []).map(useCase => (
            <div key={useCase.id} className={rowStyle}>
              <div className={rowTextStyle}>
                <span className={nameStyle}>{useCase.name}</span>
                <span className={slugStyle}>{useCase.slug}</span>
              </div>
              <div className={spacerStyle} />
              <StatusTag status={useCase.isActive ? 'success' : 'neutral'} size='sm'>
                {useCase.isActive ? 'Ativo' : 'Inativo'}
              </StatusTag>
              <Switch
                id={`use-case-${useCase.id}`}
                checked={useCase.isActive}
                aria-label={`Use case ${useCase.name} ativo`}
                disabled={toggle.isPending}
                onChange={() => {
                  setError(null);
                  toggle.mutate(useCase.id);
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <span className={hintStyle}>Nenhum use case nesta BU.</span>
      )}

      <div className={addRowStyle}>
        <UTTextField
          id='use-case-slug'
          topLabel='Slug'
          value={slug}
          placeholder='util_water'
          onChange={event => {
            setSlug(event.target.value);
          }}
        />
        <UTTextField
          id='use-case-name'
          topLabel='Nome'
          value={name}
          placeholder='Água'
          onChange={event => {
            setName(event.target.value);
          }}
        />
        <UTButton
          fitContent
          id='use-case-add'
          variant='tertiary'
          text={add.isPending ? 'Adicionando...' : 'Adicionar'}
          icon='Arrows.Plus'
          disabled={!canAdd || add.isPending}
          onClick={() => {
            setError(null);
            add.mutate();
          }}
        />
      </div>

      {error ? <span className={errorStyle}>{error}</span> : null}
    </div>
  );
};
